import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Plus, Edit2, Trash2, X, Tag, Percent, Hash, CalendarDays, ToggleLeft, ToggleRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPromoCodes = () => {
  const { token } = useAuth();
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [formData, setFormData] = useState({
    code: '',
    discount_percent: '',
    is_active: true,
    max_uses: '',
    expires_at: ''
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchPromoCodes(); }, []);

  const fetchPromoCodes = async () => {
    try {
      const res = await axios.get(`${API}/admin/promo-codes`, { headers });
      setPromoCodes(res.data.promo_codes || []);
    } catch (err) {
      console.error('Error fetching promo codes:', err);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (promo = null) => {
    if (promo) {
      setEditingPromo(promo);
      setFormData({
        code: promo.code,
        discount_percent: promo.discount_percent.toString(),
        is_active: promo.is_active,
        max_uses: promo.max_uses != null ? promo.max_uses.toString() : '',
        expires_at: promo.expires_at || ''
      });
    } else {
      setEditingPromo(null);
      setFormData({ code: '', discount_percent: '', is_active: true, max_uses: '', expires_at: '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      code: formData.code.toUpperCase().trim(),
      discount_percent: parseFloat(formData.discount_percent),
      is_active: formData.is_active,
      max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
      expires_at: formData.expires_at || null
    };

    try {
      if (editingPromo) {
        await axios.put(`${API}/admin/promo-codes/${editingPromo.id}`, payload, { headers });
      } else {
        await axios.post(`${API}/admin/promo-codes`, payload, { headers });
      }
      setShowModal(false);
      fetchPromoCodes();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error saving promo code');
    }
  };

  const handleDelete = async (id, code) => {
    if (!window.confirm(`Delete promo code "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/promo-codes/${id}`, { headers });
      fetchPromoCodes();
    } catch (err) {
      alert('Error deleting promo code');
    }
  };

  const toggleActive = async (promo) => {
    try {
      await axios.put(`${API}/admin/promo-codes/${promo.id}`, { is_active: !promo.is_active }, { headers });
      fetchPromoCodes();
    } catch (err) {
      alert('Error updating promo code');
    }
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const isMaxedOut = (promo) => {
    return promo.max_uses != null && promo.current_uses >= promo.max_uses;
  };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading promo codes...</div>;

  return (
    <div className="promo-codes-page" data-testid="admin-promo-codes">
      <style>{`
        .promo-codes-page { padding: 0; }
        .promo-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; }
        .promo-header h2 { font-size: 24px; font-weight: 600; color: #1a1918; margin: 0; }
        .btn-add-promo { display: inline-flex; align-items: center; gap: 8px; padding: 10px 20px; background: #1a1918; color: white; border: none; border-radius: 8px; font-size: 14px; font-weight: 600; cursor: pointer; transition: background 0.2s; }
        .btn-add-promo:hover { background: #333; }
        .promo-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(340px, 1fr)); gap: 20px; }
        .promo-card { background: white; border: 1px solid #e5e5e5; border-radius: 12px; padding: 24px; transition: box-shadow 0.2s; position: relative; }
        .promo-card:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.08); }
        .promo-card-header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 16px; }
        .promo-code-badge { font-size: 20px; font-weight: 700; letter-spacing: 2px; color: #1a1918; font-family: monospace; background: #f7f5f2; padding: 6px 14px; border-radius: 6px; }
        .promo-discount { font-size: 28px; font-weight: 700; color: #16a34a; }
        .promo-meta { display: flex; flex-direction: column; gap: 8px; margin-bottom: 16px; }
        .promo-meta-row { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #736c64; }
        .promo-meta-row svg { flex-shrink: 0; }
        .promo-status { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; border-radius: 20px; font-size: 12px; font-weight: 600; }
        .status-active { background: #dcfce7; color: #16a34a; }
        .status-inactive { background: #fee2e2; color: #dc2626; }
        .status-expired { background: #fef3c7; color: #d97706; }
        .status-maxed { background: #e0e7ff; color: #4f46e5; }
        .promo-actions { display: flex; gap: 8px; margin-top: 16px; border-top: 1px solid #f0f0f0; padding-top: 16px; }
        .promo-actions button { flex: 1; display: flex; align-items: center; justify-content: center; gap: 6px; padding: 8px; border: 1px solid #e5e5e5; border-radius: 8px; font-size: 13px; cursor: pointer; transition: all 0.2s; background: white; }
        .promo-actions .btn-edit:hover { border-color: #5f9dff; color: #5f9dff; }
        .promo-actions .btn-toggle:hover { border-color: #f59e0b; color: #f59e0b; }
        .promo-actions .btn-delete:hover { border-color: #dc2626; color: #dc2626; background: #fef2f2; }
        .promo-empty { text-align: center; padding: 60px 20px; color: #736c64; }
        .promo-empty h3 { font-size: 20px; color: #1a1918; margin-bottom: 8px; }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
        .modal-box { background: white; border-radius: 16px; padding: 32px; max-width: 480px; width: 100%; max-height: 90vh; overflow-y: auto; position: relative; }
        .modal-box h3 { font-size: 22px; font-weight: 600; margin: 0 0 24px; color: #1a1918; }
        .modal-close { position: absolute; top: 16px; right: 16px; background: none; border: none; cursor: pointer; color: #999; }
        .modal-close:hover { color: #333; }
        .form-group { margin-bottom: 20px; }
        .form-group label { display: block; font-size: 14px; font-weight: 600; color: #1a1918; margin-bottom: 6px; }
        .form-group input, .form-group select { width: 100%; padding: 10px 14px; border: 1px solid #e5e5e5; border-radius: 8px; font-size: 15px; outline: none; transition: border-color 0.2s; box-sizing: border-box; }
        .form-group input:focus { border-color: #5f9dff; }
        .form-group .hint { font-size: 12px; color: #999; margin-top: 4px; }
        .toggle-row { display: flex; align-items: center; gap: 12px; cursor: pointer; }
        .toggle-row .toggle-track { width: 44px; height: 24px; border-radius: 12px; position: relative; transition: background 0.2s; cursor: pointer; }
        .toggle-track.on { background: #16a34a; }
        .toggle-track.off { background: #d1d5db; }
        .toggle-track .toggle-thumb { width: 20px; height: 20px; border-radius: 50%; background: white; position: absolute; top: 2px; transition: left 0.2s; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
        .toggle-track.on .toggle-thumb { left: 22px; }
        .toggle-track.off .toggle-thumb { left: 2px; }
        .btn-submit { width: 100%; padding: 12px; background: #1a1918; color: white; border: none; border-radius: 8px; font-size: 16px; font-weight: 600; cursor: pointer; transition: background 0.2s; }
        .btn-submit:hover { background: #333; }
      `}</style>

      <div className="promo-header">
        <h2>Promo Codes</h2>
        <button className="btn-add-promo" onClick={() => openModal()} data-testid="add-promo-btn">
          <Plus size={18} /> New Promo Code
        </button>
      </div>

      {promoCodes.length === 0 ? (
        <div className="promo-empty">
          <Tag size={48} strokeWidth={1} />
          <h3>No Promo Codes Yet</h3>
          <p>Create your first promo code to offer discounts to customers.</p>
        </div>
      ) : (
        <div className="promo-grid">
          {promoCodes.map((promo) => (
            <div key={promo.id} className="promo-card" data-testid={`promo-card-${promo.id}`}>
              <div className="promo-card-header">
                <span className="promo-code-badge">{promo.code}</span>
                <span className="promo-discount">-{promo.discount_percent}%</span>
              </div>

              <div className="promo-meta">
                <div className="promo-meta-row">
                  <Hash size={14} />
                  <span>Used {promo.current_uses}{promo.max_uses != null ? ` / ${promo.max_uses}` : ''} times</span>
                </div>
                {promo.expires_at && (
                  <div className="promo-meta-row">
                    <CalendarDays size={14} />
                    <span>Expires {promo.expires_at}</span>
                  </div>
                )}
              </div>

              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {promo.is_active && !isExpired(promo.expires_at) && !isMaxedOut(promo) && (
                  <span className="promo-status status-active">Active</span>
                )}
                {!promo.is_active && <span className="promo-status status-inactive">Inactive</span>}
                {isExpired(promo.expires_at) && <span className="promo-status status-expired">Expired</span>}
                {isMaxedOut(promo) && <span className="promo-status status-maxed">Max Used</span>}
              </div>

              <div className="promo-actions">
                <button className="btn-edit" onClick={() => openModal(promo)} data-testid={`edit-promo-${promo.id}`}>
                  <Edit2 size={14} /> Edit
                </button>
                <button className="btn-toggle" onClick={() => toggleActive(promo)} data-testid={`toggle-promo-${promo.id}`}>
                  {promo.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                  {promo.is_active ? 'Deactivate' : 'Activate'}
                </button>
                <button className="btn-delete" onClick={() => handleDelete(promo.id, promo.code)} data-testid={`delete-promo-${promo.id}`}>
                  <Trash2 size={14} /> Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}><X size={20} /></button>
            <h3>{editingPromo ? 'Edit Promo Code' : 'New Promo Code'}</h3>

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. SUMMER20"
                  required
                  style={{ textTransform: 'uppercase', letterSpacing: '2px', fontFamily: 'monospace' }}
                  data-testid="promo-code-input"
                />
              </div>

              <div className="form-group">
                <label>Discount Percentage *</label>
                <input
                  type="number"
                  min="1"
                  max="100"
                  step="1"
                  value={formData.discount_percent}
                  onChange={(e) => setFormData({ ...formData, discount_percent: e.target.value })}
                  placeholder="e.g. 20"
                  required
                  data-testid="promo-discount-input"
                />
                <div className="hint">Enter a value between 1 and 100</div>
              </div>

              <div className="form-group">
                <label>Max Uses</label>
                <input
                  type="number"
                  min="1"
                  value={formData.max_uses}
                  onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                  placeholder="Leave empty for unlimited"
                  data-testid="promo-max-uses-input"
                />
              </div>

              <div className="form-group">
                <label>Expiration Date</label>
                <input
                  type="date"
                  value={formData.expires_at}
                  onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                  data-testid="promo-expires-input"
                />
                <div className="hint">Leave empty for no expiration</div>
              </div>

              <div className="form-group">
                <label>Status</label>
                <div className="toggle-row" onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}>
                  <div className={`toggle-track ${formData.is_active ? 'on' : 'off'}`}>
                    <div className="toggle-thumb" />
                  </div>
                  <span style={{ fontWeight: 500 }}>{formData.is_active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>

              <button type="submit" className="btn-submit" data-testid="promo-submit-btn">
                {editingPromo ? 'Update Promo Code' : 'Create Promo Code'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPromoCodes;
